import {
  CButton,
  CCol,
  CFormCheck,
  CFormFeedback,
  CFormInput,
  CFormLabel,
  CFormTextarea,
  CLoadingButton,
  CRow,
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilArrowRight } from '@coreui/icons'
import React, { useState } from 'react'
import PlayerService from 'src/service/PlayerService'
import ToastComponent from 'src/components/common/TaostComponent'
import { resolvePlayerImageUrl } from 'src/utils/imageUrl'

const SUPPORTED_FORMATS = ['image/jpeg', 'image/png', 'image/webp']
const MAX_IMAGE_SIZE = 2 * 1024 * 1024

const DEFAULT_AVATARS = { 1: 'Position1a', 2: 'Position2a', 3: 'Position3a', 4: 'Position4a' }

const ProfileTab = ({ detail, saveEditPlayer, onSaved, onCancel, onToggleStatus }) => {
  const player = detail.player_data
  const [loader, setLoader] = useState(false)
  const [isAvatar, setIsAvatar] = useState(player.is_avatar === 1 ? 1 : 0)
  const [selectedAvatar, setSelectedAvatar] = useState(
    player.is_avatar === 1
      ? player.image?.split('.').slice(0, -1).join('.')
      : DEFAULT_AVATARS[player.position] || 'Position1a',
  )
  const [imageFile, setImageFile] = useState(null)
  const [imageError, setImageError] = useState('')
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null)
  const [externalProfileLabel, setExternalProfileLabel] = useState(
    player.external_profile_label || '',
  )
  const [externalProfileLink, setExternalProfileLink] = useState(player.external_profile_link || '')
  const [description, setDescription] = useState(player.description || '')

  const currentImageUrl = resolvePlayerImageUrl(player)
  const newAvatarPreviewUrl =
    isAvatar === 1 ? `${process.env.REACT_APP_PLAYER_AVATAR_URL}${selectedAvatar}.png` : null
  const newPreviewUrl = isAvatar === 1 ? newAvatarPreviewUrl : imagePreviewUrl

  const handleImageFile = (file) => {
    if (!file) return
    if (!SUPPORTED_FORMATS.includes(file.type)) {
      setImageError('Invalid file format, only JPG, PNG or WEBP is allowed')
      return
    }
    if (file.size > MAX_IMAGE_SIZE) {
      setImageError('File size too large, max file size is 2 MB')
      return
    }
    setImageError('')
    setImageFile(file)
    const reader = new FileReader()
    reader.onload = () => setImagePreviewUrl(reader.result)
    reader.readAsDataURL(file)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoader(true)
    try {
      const imageForm = new FormData()
      imageForm.append('playerId', player.id)
      imageForm.append('avatar_image_name', selectedAvatar)
      imageForm.append('is_avatar', isAvatar)
      if (isAvatar !== 1 && imageFile) {
        imageForm.append('image', imageFile)
      }
      const imageRes = await PlayerService.editPlayerImage(imageForm)
      if (imageRes.status !== 200) {
        ToastComponent(imageRes.message, 'error')
        setLoader(false)
        return
      }

      const res = await saveEditPlayer({
        external_profile_label: externalProfileLabel,
        external_profile_link: externalProfileLink,
        description,
      })
      if (res.status !== 200) {
        ToastComponent(res.message, 'error')
        setLoader(false)
        return
      }
      ToastComponent('Profile updated successfully.', 'success')
      onSaved()
    } catch (e2) {
      ToastComponent('Something went wrong', 'error')
    }
    setLoader(false)
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="fw-bold mb-2">Profile Image</div>

      <div className="d-flex align-items-center mb-3">
        <CFormCheck
          button={{ color: 'secondary', variant: 'outline' }}
          type="radio"
          name="image-source"
          id="image-source-avatar"
          autoComplete="off"
          label="Avatar"
          checked={isAvatar === 1}
          onChange={() => setIsAvatar(1)}
        />
        &nbsp;
        <CFormCheck
          button={{ color: 'secondary', variant: 'outline' }}
          type="radio"
          name="image-source"
          id="image-source-upload"
          autoComplete="off"
          label="Upload Image"
          checked={isAvatar !== 1}
          onChange={() => setIsAvatar(0)}
        />
      </div>

      {isAvatar === 1 ? (
        <CRow className="g-2 mb-3">
          {['a', 'b'].map((suffix) => {
            const name = `${DEFAULT_AVATARS[player.position] || 'Position1a'}`.replace(/a$/, suffix)
            const url = `${process.env.REACT_APP_PLAYER_AVATAR_URL}${name}.png`
            return (
              <CCol xs="auto" key={name}>
                <img
                  src={url}
                  alt={name}
                  width={56}
                  height={56}
                  onClick={() => setSelectedAvatar(name)}
                  className={
                    'rounded-circle' +
                    (selectedAvatar === name ? ' border border-primary border-2' : ' border')
                  }
                  style={{ cursor: 'pointer' }}
                />
              </CCol>
            )
          })}
        </CRow>
      ) : (
        <CCol md={12} className="mb-3">
          <CFormLabel htmlFor="profile-image">Choose Image</CFormLabel>
          <CFormInput
            type="file"
            id="profile-image"
            accept="image/jpeg,image/png,image/webp"
            onChange={(e) => handleImageFile(e.target.files[0])}
          />
          <div className="text-medium-emphasis small mt-1">JPG, PNG or WEBP up to 2MB</div>
          {imageError && (
            <CFormFeedback invalid className="d-block">
              {imageError}
            </CFormFeedback>
          )}
        </CCol>
      )}

      <div className="d-flex align-items-center gap-3 mb-4">
        <div className="text-center">
          <div className="text-medium-emphasis small mb-1">Current</div>
          {currentImageUrl ? (
            <img
              src={currentImageUrl}
              alt="Current"
              width={64}
              height={64}
              className="rounded-circle"
            />
          ) : (
            <div
              className="rounded-circle bg-light d-flex align-items-center justify-content-center"
              style={{ width: 64, height: 64 }}
            />
          )}
        </div>
        <CIcon icon={cilArrowRight} className="text-medium-emphasis" />
        <div className="text-center">
          <div className="text-medium-emphasis small mb-1">New</div>
          {newPreviewUrl ? (
            <img src={newPreviewUrl} alt="New" width={64} height={64} className="rounded-circle" />
          ) : (
            <div
              className="rounded-circle bg-light d-flex align-items-center justify-content-center"
              style={{ width: 64, height: 64 }}
            />
          )}
        </div>
      </div>

      <hr className="my-4" />

      <div className="fw-bold mb-2">Public Profile</div>
      <CRow className="g-3">
        <CCol md={6}>
          <CFormLabel htmlFor="external_profile_label">External Profile Label</CFormLabel>
          <CFormInput
            id="external_profile_label"
            placeholder="e.g. View Player Profile"
            value={externalProfileLabel}
            onChange={(e) => setExternalProfileLabel(e.target.value)}
          />
        </CCol>
        <CCol md={6}>
          <CFormLabel htmlFor="external_profile_link">External Profile Link</CFormLabel>
          <CFormInput
            id="external_profile_link"
            placeholder="https://"
            value={externalProfileLink}
            onChange={(e) => setExternalProfileLink(e.target.value)}
          />
        </CCol>
        <CCol md={12}>
          <CFormLabel htmlFor="description">Description</CFormLabel>
          <CFormTextarea
            id="description"
            rows={3}
            placeholder="Add a short player description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </CCol>
      </CRow>

      <div className="d-flex justify-content-between align-items-center mt-4">
        <CButton
          color="danger"
          variant="ghost"
          type="button"
          onClick={() => onToggleStatus(player.is_active === 1 ? 0 : 1)}
        >
          {player.is_active === 1 ? 'Deactivate' : 'Activate'}
        </CButton>
        <div className="d-flex gap-2">
          <CButton color="secondary" variant="outline" type="button" onClick={onCancel}>
            Cancel
          </CButton>
          <CLoadingButton type="submit" color="primary" loading={loader}>
            Save Profile
          </CLoadingButton>
        </div>
      </div>
    </form>
  )
}

export default ProfileTab
